import {
  AdminPeoplePage,
  AwardRegister,
  FinalSummaries,
  HomeRequester,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { request } from './client';

// Reads the role Homes add on top of `homeQuery` (WP-HOMES-2). Each card loads its own and shows
// a designed state when a read is refused, so one card never fails the whole Home.

/** Only the register's totals: Home needs the count, not the rows. */
const RegisterTotals = AwardRegister.pick({ totals: true });

/**
 * Whether any award has ever reached the Stock Admin team (the register over all time, from that
 * stage on, one row): tells an empty queue's first day from "all caught up" (DESIGN-SPEC §5).
 * Readers of All awards only.
 */
export const anyAwardsQuery = queryOptions({
  queryKey: ['homes', 'any-awards'] as const,
  queryFn: async ({ signal }) => {
    const reached = 'stage=with_stock_admin,employee_choice,paid,closed';
    const register = await request(`/api/v1/insights/awards?period=any_time&${reached}&size=1`, {
      schema: RegisterTotals,
      signal,
    });
    return register.totals.awards > 0;
  },
  staleTime: 5 * 60_000,
  retry: false,
});

/** Only the People directory's counts: Home needs how many, not who. */
const PeopleCounts = AdminPeoplePage.pick({ counts: true });

/**
 * How many people still need their app-owned directory fields (sector, home org, OH/G&A) before
 * their award lines can be routed and coded: the Stock Admin's readiness checklist (FR4). Stock
 * Admins and System Admins only; refused for everyone else.
 */
export const needsSetupQuery = queryOptions({
  queryKey: ['homes', 'needs-setup'] as const,
  queryFn: async ({ signal }) => {
    const page = await request('/api/v1/admin/people?status=needs_setup&size=1', {
      schema: PeopleCounts,
      signal,
    });
    return page.counts.needsSetup;
  },
  staleTime: 5 * 60_000,
  retry: false,
});

/**
 * X1's Final summaries card: the latest closed Executive Discretionary Bonuses, newest close
 * first (`GET /api/v1/insights/final-summaries`, APP-072). All awards' readers; empty in Preview.
 */
export function finalSummariesQuery(limit: number) {
  return queryOptions({
    queryKey: ['homes', 'final-summaries', limit] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/insights/final-summaries?limit=${String(limit)}`, {
        schema: FinalSummaries,
        signal,
      }),
    staleTime: 60_000,
  });
}

/**
 * R1's side cards (`GET /api/v1/home/requester`): this year's Executive pool and the Stock Admin
 * team's names and titles. Executives and pre-approved requesters only (APP-054).
 */
export const requesterHomeQuery = queryOptions({
  queryKey: ['homes', 'requester'] as const,
  queryFn: ({ signal }) => request('/api/v1/home/requester', { schema: HomeRequester, signal }),
  staleTime: 60_000,
  retry: false,
});
